import { useTranslation } from 'react-i18next';
import { formatRate, type Currency, type QuoteTotals as Totals } from '@worki/shared';
import { currentLocale } from '../i18n.js';
import { money } from '../lib/format.js';

/**
 * The totals block, rendered identically by the editor's live preview, the
 * owner's detail page and the customer's public page. It lives here because a
 * quote that shows one set of rows on screen and another on the PDF is a bug
 * the three copies of this markup used to make easy.
 *
 * The VAT rows come from the quote's own snapshot (`totals.vatRate`), never
 * from the business's current setting, so a document already sent keeps
 * displaying the tax it was sent with.
 */
export function QuoteTotalsPanel({
  totals,
  currency,
  discountLabel,
  pricesIncludeVat,
  className,
  style,
}: {
  totals: Totals;
  currency: Currency;
  discountLabel?: string | null;
  pricesIncludeVat?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const hasVat = totals.vatRate > 0;

  return (
    <div className={className ? `totals ${className}` : 'totals'} style={style}>
      <div className="totals__row">
        <span>{t('quotes.subtotal')}</span>
        <span className="ltr-nums">{money(totals.subtotal, currency, locale)}</span>
      </div>
      {totals.discount > 0 && (
        <div className="totals__row">
          <span>{discountLabel || t('quotes.discount')}</span>
          <span className="ltr-nums">-{money(totals.discount, currency, locale)}</span>
        </div>
      )}
      {/* A quote with no VAT — an עוסק פטור, or one issued before VAT support —
          prints neither row rather than a zero the reader has to interpret. */}
      {hasVat && (
        <>
          <div className="totals__row">
            <span>{t('quotes.net')}</span>
            <span className="ltr-nums">{money(totals.net, currency, locale)}</span>
          </div>
          <div className="totals__row">
            <span>{t('quotes.vat', { rate: formatRate(totals.vatRate) })}</span>
            <span className="ltr-nums">{money(totals.vat, currency, locale)}</span>
          </div>
        </>
      )}
      <div className="totals__row totals__row--grand text-green-700 dark:text-green-400">
        <span style={{ color: '#15803d' }}>{t('quotes.total')}</span>
        <span className="ltr-nums" style={{ color: '#15803d' }}>{money(totals.total, currency, locale)}</span>
      </div>
      {hasVat && pricesIncludeVat && <p className="totals__note">{t('quotes.vatIncluded')}</p>}
    </div>
  );
}
